import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';

import { config } from '@/app/config/env';

/** Per-route title, declared on the route's `handle`. */
export interface RouteHandle {
  title?: string;
}

function readTitle(handle: unknown): string | undefined {
  if (typeof handle !== 'object' || handle === null) return undefined;
  const value = (handle as RouteHandle).title;
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Keeps the tab title in step with the active route.
 *
 * Renders nothing — it exists so the title is owned in one place rather than
 * each page setting `document.title` in its own effect and racing the others
 * on a fast navigation. The deepest matched route with a `title` handle wins,
 * so a nested route can override its parent.
 */
export function DocumentTitle() {
  const matches = useMatches();

  useEffect(() => {
    const pageTitle = matches.reduceRight<string | undefined>(
      (found, match) => found ?? readTitle(match.handle),
      undefined,
    );

    document.title = pageTitle ? `${pageTitle} · ${config.appName}` : config.appName;
  }, [matches]);

  return null;
}
